import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, X, Video as VideoIcon, User as UserIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSearchIndex, runSearch, type SearchResults } from "@/hooks/useSearchIndex";
import { logSearch, logSearchClick } from "@/lib/searchLogger";
import i18n from "@/i18n";
import { getCategoryColor, readableTextOn } from "@/lib/categoryColors";

type Variant = "desktop" | "mobile";

interface HeaderSearchProps {
  variant?: Variant;
  onCloseMobile?: () => void;
  compact?: boolean;
}

const HeaderSearch = ({ variant = "desktop", onCloseMobile, compact = false }: HeaderSearchProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { data: index } = useSearchIndex();
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debouncedQuery = useDebouncedValue(value, 180);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastLogIdRef = useRef<string | null>(null);
  const lastLoggedQueryRef = useRef<string>("");

  const locale = (i18n.language || "en").slice(0, 2);
  const results: SearchResults = useMemo(
    () => runSearch(index, debouncedQuery, locale),
    [index, debouncedQuery, locale],
  );

  // Flat list for keyboard nav
  const flatItems = useMemo(() => {
    const items: Array<{ type: "video" | "creator" | "category"; id: string; href: string; label: string }> = [];
    for (const v of results.videos) items.push({ type: "video", id: v.id, href: `/videos/${v.slug || v.id}`, label: (locale === "es" && v.title_es) || v.title });
    for (const c of results.creators) items.push({ type: "creator", id: c.id, href: `/creators/${c.slug}`, label: c.display_name });
    for (const c of results.categories) items.push({ type: "category", id: c.id, href: `/videos?category=${c.slug}`, label: (locale === "es" && c.name_es) || c.name });
    return items;
  }, [results, locale]);

  // Fire-and-forget log when debounced query changes and yields something
  useEffect(() => {
    const q = debouncedQuery.trim();
    if (q.length < 2) return;
    if (q === lastLoggedQueryRef.current) return;
    lastLoggedQueryRef.current = q;
    logSearch({ query: q, locale, resultCount: results.total }).then((id) => {
      lastLogIdRef.current = id;
    });
  }, [debouncedQuery, results.total, locale]);

  // Outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Escape to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        if (variant === "mobile") onCloseMobile?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [variant, onCloseMobile]);

  useEffect(() => {
    if (variant === "mobile") {
      // Autofocus when mobile input mounts
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [variant]);

  const handleSelect = useCallback(
    (item: { type: "video" | "creator" | "category"; id: string; href: string }) => {
      // Fire click log (best-effort)
      logSearchClick(lastLogIdRef.current, item.type, item.id);
      setOpen(false);
      setValue("");
      if (variant === "mobile") onCloseMobile?.();
      navigate(item.href);
    },
    [navigate, variant, onCloseMobile],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(flatItems.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(-1, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = flatItems[activeIdx] ?? flatItems[0];
      if (item) handleSelect(item);
    }
  };

  const hasResults = results.total > 0;
  const showDropdown = open && debouncedQuery.trim().length >= 2;

  return (
    <div
      ref={containerRef}
      className={
        variant === "desktop"
          ? compact
            ? "relative w-[150px] xl:w-[180px] min-w-0 shrink"
            : "relative w-[160px] xl:w-[210px] min-w-0 shrink"
          : "relative w-full"
      }
    >
      <div className="relative overflow-hidden">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setOpen(true);
            setActiveIdx(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t("search_box.placeholder", "What do you want to learn today?")}
          className="w-full min-w-0 pl-9 pr-9 h-10 rounded-full bg-card border-border text-foreground placeholder:text-muted-foreground focus-visible:ring-1"
          aria-label={t("search_box.aria", "Search")}
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              setValue("");
              setActiveIdx(-1);
              inputRef.current?.focus();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"
            aria-label={t("search_box.clear", "Clear")}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-background border border-border rounded-lg shadow-strong overflow-hidden z-50 max-h-[70vh] overflow-y-auto">
          {!hasResults ? (
            <div className="px-4 py-6 text-sm text-muted-foreground text-center">
              {t("search_box.no_results", "No matches found")}
            </div>
          ) : (
            <>
              {results.videos.length > 0 && (
                <div className="py-1">
                  <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("search_box.videos", "Videos")}
                  </div>
                  {results.videos.map((v, i) => {
                    const idx = i;
                    const active = activeIdx === idx;
                    const title = (locale === "es" && v.title_es) || v.title;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={() => handleSelect({ type: "video", id: v.id, href: `/videos/${v.slug || v.id}` })}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted transition-colors ${active ? "bg-muted" : ""}`}
                      >
                        <div className="flex-shrink-0 w-14 h-9 rounded overflow-hidden bg-muted flex items-center justify-center">
                          {v.thumbnail_url ? (
                            <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <VideoIcon className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                        <span className="flex-1 truncate text-sm text-foreground">{title}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {results.creators.length > 0 && (
                <div className="py-1 border-t border-border">
                  <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("search_box.creators", "Creators")}
                  </div>
                  {results.creators.map((c, i) => {
                    const idx = results.videos.length + i;
                    const active = activeIdx === idx;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={() => handleSelect({ type: "creator", id: c.id, href: `/creators/${c.slug}` })}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted transition-colors ${active ? "bg-muted" : ""}`}
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                          {c.profile_image_url ? (
                            <img src={c.profile_image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                          ) : (
                            <UserIcon className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                        <span className="flex-1 truncate text-sm text-foreground">{c.display_name}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {results.categories.length > 0 && (
                <div className="py-2 px-3 border-t border-border">
                  <div className="pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("search_box.categories", "Categories")}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {results.categories.map((c, i) => {
                      const bg = getCategoryColor(c, i);
                      const fg = readableTextOn(bg);
                      const label = (locale === "es" && c.name_es) || c.name;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() =>
                            handleSelect({ type: "category", id: c.id, href: `/videos?category=${c.slug}` })
                          }
                          style={{ backgroundColor: bg, color: fg }}
                          className="px-2 py-0.5 rounded text-[11px] font-semibold hover:opacity-90 transition-opacity"
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default HeaderSearch;
